import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Flag, Search, Filter, Loader, CheckCircle, 
  XCircle, ExternalLink, AlertTriangle, Download,
  FileText, User, Calendar, MessageSquare, UserX, Star,
  Clock, Ban, Check, AlertCircle
} from 'lucide-react';
import toast from 'react-hot-toast';
import AuthContext from '../../contexts/AuthContext';

/**
 * Enhanced Moderator Reports Page component - Updated for your schema
 * Shows all types of reported content for the moderator's school
 */
const ModeratorReportsPage = () => {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('pending');
  const [reportTypeFilter, setReportTypeFilter] = useState('all'); // 'all', 'material', 'comment', 'user', 'professor_rating'
  const [showFilters, setShowFilters] = useState(false);
  const [resolveConfirmation, setResolveConfirmation] = useState(null);
  const [reportCounts, setReportCounts] = useState({
    all: 0,
    material: 0,
    comment: 0,
    user: 0,
    professor_rating: 0,
    flagged_materials: 0
  });
  const [userAction, setUserAction] = useState('none');
  const [suspensionDays, setSuspensionDays] = useState(7);
  
  useEffect(() => {
    // Redirect if user is not moderator
    if (user && user.role !== 'moderator') {
      toast.error('You do not have permission to access this page');
      navigate('/school');
      return;
    }
    
    // Fetch data if user is moderator
    if (user && user.role === 'moderator' && user.school?.id) {
      fetchReports();
      fetchReportCounts();
    }
  }, [user, navigate, statusFilter, reportTypeFilter]);
  
  const fetchReports = async () => {
    try {
      setLoading(true);
      
      const schoolId = user.school?.id || user.schoolId;
      
      if (!schoolId) {
        throw new Error('School information not found');
      }
      
      // Build query parameters
      const params = new URLSearchParams({
        limit: '50'
      });

      // Handle special filter types
      if (reportTypeFilter === 'flagged_materials') {
        params.append('flaggedMaterialsOnly', 'true');
      } else {
        params.append('status', statusFilter);
        params.append('reportType', reportTypeFilter);
      }
      
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/schools/${schoolId}/reports?${params}`,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        }
      );
      
      if (!response.ok) {
        throw new Error('Failed to fetch reports');
      }
      
      const data = await response.json();
      setReports(data.reports || []);
    } catch (error) {
      console.error('Error fetching reports:', error);
      setError(error.message);
      toast.error('Failed to load reports');
      setReports([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchReportCounts = async () => {
    try {
      const schoolId = user.school?.id || user.schoolId;
      if (!schoolId) return;

      // Fetch counts for each report type (pending only for badges)
      const promises = [
        fetch(`${process.env.REACT_APP_API_URL}/api/schools/${schoolId}/reports?status=pending&reportType=all&limit=0`, {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        }),
        fetch(`${process.env.REACT_APP_API_URL}/api/schools/${schoolId}/reports?status=pending&reportType=material&limit=0`, {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        }),
        fetch(`${process.env.REACT_APP_API_URL}/api/schools/${schoolId}/reports?status=pending&reportType=comment&limit=0`, {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        }),
        fetch(`${process.env.REACT_APP_API_URL}/api/schools/${schoolId}/reports?status=pending&reportType=user&limit=0`, {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        }),
        fetch(`${process.env.REACT_APP_API_URL}/api/schools/${schoolId}/reports?status=pending&reportType=professor_rating&limit=0`, {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        }),
        // NEW: Fetch flagged materials count (pending only for badge)
        fetch(`${process.env.REACT_APP_API_URL}/api/schools/${schoolId}/reports?flaggedMaterialsOnly=true&status=pending&limit=0`, {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        })
      ];

      const responses = await Promise.all(promises);
      const data = await Promise.all(responses.map(r => r.json()));

      setReportCounts({
        all: data[0].total || 0,
        material: data[1].total || 0,
        comment: data[2].total || 0,
        user: data[3].total || 0,
        professor_rating: data[4].total || 0,
        flagged_materials: data[5].total || 0
      });
    } catch (error) {
      console.error('Error fetching report counts:', error);
    }
  };
  
  const handleResolveReport = async (reportId, resolution) => {
    try {
      const requestBody = { 
        action: resolution, 
        note: resolveConfirmation.note || '',
        userAction: resolveConfirmation.reportType === 'user' ? userAction : 'none',
        suspensionDays: parseInt(suspensionDays, 10) || 7
      };

      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/reports/${reportId}/resolve`,
        {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(requestBody)
        }
      );
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to resolve report');
      }
      
      const newStatus = resolution === 'approve' ? 'approved' : 'rejected';
      setReports(reports.map(report => 
        report.id === reportId 
          ? { ...report, status: newStatus, resolvedAt: new Date().toISOString() } 
          : report
      ));
      
      setResolveConfirmation(null);
      
      // Update counts
      fetchReportCounts();
      
      toast.success(`Report ${resolution === 'approve' ? 'approved' : 'rejected'} successfully`);
    } catch (error) {
      console.error('Error resolving report:', error);
      toast.error(error.message || 'Failed to resolve report');
    }
  };
  
  // Filter reports based on search query
  const filteredReports = Array.isArray(reports) ? reports.filter(report => {
    let searchText = '';
    
    if (report.reportType === 'material') {
      searchText = `${report.itemDetails?.title || ''} ${report.itemDetails?.courseName || ''} ${report.reason || ''}`;
    } else if (report.reportType === 'comment') {
      searchText = `${report.itemDetails?.content || ''} ${report.itemDetails?.materialTitle || ''} ${report.reason || ''}`;
    } else if (report.reportType === 'user') {
      searchText = `${report.itemDetails?.name || ''} ${report.itemDetails?.email || ''} ${report.reason || ''}`;
    } else if (report.reportType === 'professor_rating') {
      searchText = `${report.itemDetails?.professorName || ''} ${report.itemDetails?.review || ''} ${report.itemDetails?.ratingAuthorName || ''} ${report.reason || ''}`;
    }
    
    searchText += ` ${report.reporter?.name || ''}`;
    
    return searchText.toLowerCase().includes(searchQuery.toLowerCase());
  }) : [];
  
  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    
    const date = new Date(dateString);
    return date.toLocaleDateString(undefined, { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };
  
  // Get status badge
  const getStatusBadge = (status) => {
    switch(status) {
      case 'approved':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">
            <CheckCircle size={12} className="mr-1" />
            Approved
          </span>
        );
      case 'rejected':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200">
            <XCircle size={12} className="mr-1" />
            Rejected
          </span>
        );
      case 'pending':
      default:
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200">
            <AlertTriangle size={12} className="mr-1" />
            Pending
          </span>
        );
    }
  };

  // Get report type icon and details
  const getReportTypeInfo = (reportType) => {
    switch(reportType) {
      case 'material':
        return { icon: FileText, label: 'Material', color: 'text-blue-500' };
      case 'comment':
        return { icon: MessageSquare, label: 'Comment', color: 'text-green-500' };
      case 'user':
        return { icon: UserX, label: 'User', color: 'text-red-500' };
      case 'professor_rating':
        return { icon: Star, label: 'Professor Rating', color: 'text-yellow-500' };
      default:
        return { icon: Flag, label: 'Report', color: 'text-gray-500' };
    }
  };

  // Tab with badge component
  const TabButton = ({ type, label, icon: Icon, count, active, onClick, variant = 'default' }) => (
    <button
      onClick={onClick}
      className={`relative px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
        active 
          ? variant === 'flagged'
            ? 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200 border-2 border-orange-300 dark:border-orange-700'
            : 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200' 
          : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200 hover:bg-gray-200 dark:hover:bg-gray-600'
      }`}
    >
      <div className="flex items-center space-x-2">
        <Icon size={16} />
        <span>{label}</span>
        {count > 0 && (
          <span className={`inline-flex items-center justify-center px-2 py-1 text-xs font-bold leading-none text-white rounded-full ${
            variant === 'flagged' ? 'bg-orange-500' : 'bg-red-500'
          }`}>
            {count}
          </span>
        )}
      </div>
    </button>
  );

  // Add this function to your ReportsPage component
  const getUserStatusBadge = (status) => {
    switch(status) {
      case 'suspended':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200">
            <Clock size={12} className="mr-1" />
            Suspended
          </span>
        );
      case 'banned':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200">
            <Ban size={12} className="mr-1" />
            Banned
          </span>
        );
      case 'active':
      default:
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">
            <Check size={12} className="mr-1" />
            Active
          </span>
        );
    }
  };

  // Render report content based on type
  const renderReportContent = (report) => {
    const typeInfo = getReportTypeInfo(report.reportType);
    const Icon = typeInfo.icon;

    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden border border-gray-200 dark:border-gray-700">
        <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <div className="flex items-center">
              <Icon className={`h-5 w-5 ${typeInfo.color} mr-2`} />
              <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                {report.reportType === 'material' && (report.itemDetails?.title || 'Untitled Material')}
                {report.reportType === 'comment' && 'Comment Report'}
                {report.reportType === 'user' && `User: ${report.itemDetails?.name || 'Unknown User'}`}
                {report.reportType === 'professor_rating' && `Professor Rating: ${report.itemDetails?.professorName || 'Unknown Professor'}`}
              </h3>
              {/* NEW: Show flagged badge for auto-flagged materials */}
              {reportTypeFilter === 'flagged_materials' && report.reporter?.name === 'System' && (
                <div className="ml-3">
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200">
                    <AlertCircle size={12} className="mr-1" />
                    Automatically Flagged
                  </span>
                </div>
              )}
            </div>
            <div className="flex items-center mt-1 text-sm text-gray-500 dark:text-gray-400">
              <span className="mr-4">Type: {typeInfo.label}</span>
              {report.reportType === 'material' && (
                <span>Course: {report.itemDetails?.courseName || 'Unknown'}</span>
              )}
              {report.reportType === 'comment' && (
                <span>On: {report.itemDetails?.materialTitle || 'Unknown Material'}</span>
              )}
              {report.reportType === 'user' && (
                <span>Email: {report.itemDetails?.email || 'Unknown'}</span>
              )}
              {report.reportType === 'professor_rating' && (
                <span>Rating: {report.itemDetails?.rating || 'N/A'}/5 by {report.itemDetails?.ratingAuthorName || 'Unknown'}</span>
              )}
            </div>
          </div>
          
          <div className="flex items-center">
            {getStatusBadge(report.status)}
            
            {report.reportType === 'material' && report.itemDetails?.fileUrl && (
              <a 
                href={`${process.env.REACT_APP_API_URL}${report.itemDetails.fileUrl?.startsWith('/') ? '' : '/'}${report.itemDetails.fileUrl}`}
                target="_blank" 
                rel="noopener noreferrer"
                className="ml-3 inline-flex items-center px-3 py-1 border border-gray-300 dark:border-gray-600 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                <Download className="h-4 w-4 mr-1" />
                View Material
              </a>
            )}
          </div>
        </div>
        
        <div className="p-4">
          {/* NEW: Special display for flagged materials */}
          {reportTypeFilter === 'flagged_materials' && report.reportType === 'material' && (
            <div className="mb-4">
              <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Compliance Violation Details:
              </h4>
              <div className="bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800 rounded-md p-3">
                <div className="text-sm text-orange-800 dark:text-orange-200">
                  <strong>Flagged for:</strong> {report.reason || 'Academic integrity policy violation'}
                </div>
                <div className="text-xs text-orange-600 dark:text-orange-300 mt-1">
                  This material was automatically flagged by AI analysis for potential violations of academic integrity policies.
                </div>
              </div>
            </div>
          )}

          {/* Report-specific content */}
          {report.reportType === 'comment' && (
            <div className="mb-4">
              <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Reported Content:
              </h4>
              <p className="text-sm text-gray-600 dark:text-gray-400 bg-gray-50 dark:bg-gray-700 p-3 rounded-md">
                {report.itemDetails?.content || 'No content available'}
              </p>
            </div>
          )}

          {report.reportType === 'professor_rating' && (
            <div className="mb-4">
              <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Reported Rating:
              </h4>
              <div className="bg-gray-50 dark:bg-gray-700 p-3 rounded-md">
                <div className="flex items-center mb-2">
                  <div className="flex items-center mr-3">
                    {Array.from({ length: 5 }, (_, i) => (
                      <Star
                        key={i}
                        size={16}
                        className={`${
                          i < (report.itemDetails?.rating || 0)
                            ? 'text-yellow-500 fill-current'
                            : 'text-gray-300 dark:text-gray-600'
                        }`}
                      />
                    ))}
                  </div>
                  <span className="text-sm font-medium text-gray-900 dark:text-white">
                    {report.itemDetails?.rating || 'N/A'}/5
                  </span>
                </div>
                {report.itemDetails?.review && (
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    "{report.itemDetails.review}"
                  </p>
                )}
              </div>
            </div>
          )}

          {report.reportType === 'user' && (
            <div className="mb-4">
              <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                User Report History:
              </h4>
              <div className="bg-white dark:bg-gray-800 p-3 rounded-md">
                <div className="flex items-center mb-2">
                  <span className="text-sm font-medium text-gray-900 dark:text-white mr-2">
                    Current Status:
                  </span>
                  {getUserStatusBadge(report.itemDetails?.status || 'active')}
                  {report.itemDetails?.status === 'suspended' && report.itemDetails?.statusUntil && (
                    <span className="text-xs text-gray-500 dark:text-gray-400 ml-2">
                      Until {formatDate(report.itemDetails.statusUntil)}
                    </span>
                  )}
                </div>
                <div className="mb-2">
                  <span className="text-sm font-medium text-gray-900 dark:text-white mr-2">
                    Total Reports:
                  </span>
                  <span className="px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300">
                    {report.itemDetails?.reportCount || 0}
                  </span>
                </div>
                {report.itemDetails?.reportCount > 0 && (
                  <div className="text-xs text-red-600 dark:text-red-400">
                    This user has been reported multiple times.
                  </div>
                )}
              </div>
            </div>
          )}

          <div className="mb-4">
            <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Report Reason:
            </h4>
            <p className="text-sm text-gray-600 dark:text-gray-400 bg-gray-50 dark:bg-gray-700 p-3 rounded-md">
              {report.reason || 'No reason provided'}
            </p>
          </div>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
            <div>
              <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Reported By:
              </h4>
              <div className="flex items-center">
                <User className="h-4 w-4 text-gray-500 dark:text-gray-400 mr-2" />
                <span className="text-sm text-gray-600 dark:text-gray-400">
                  {report.reporter?.name || 'Unknown User'}
                </span>
              </div>
            </div>
            
            <div>
              <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Report Date:
              </h4>
              <div className="flex items-center">
                <Calendar className="h-4 w-4 text-gray-500 dark:text-gray-400 mr-2" />
                <span className="text-sm text-gray-600 dark:text-gray-400">
                  {formatDate(report.createdAt)}
                </span>
              </div>
            </div>
          </div>
          
          {report.status !== 'pending' && (
            <div>
              <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Resolution:
              </h4>
              <div className="flex items-center">
                <span className="text-sm text-gray-600 dark:text-gray-400 mr-2">
                  {report.status === 'approved' ? 
                    `${typeInfo.label} ${report.reportType === 'user' || report.reportType === 'professor_rating' ? 'removed' : 'removed'}` : 
                    `${typeInfo.label} kept`
                  } by {report.resolvedBy?.name || 'Unknown'} on {formatDate(report.resolvedAt)}
                </span>
              </div>
              {report.moderatorNote && (
                <div className="mt-2">
                  <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Note:
                  </h4>
                  <p className="text-sm text-gray-600 dark:text-gray-400 bg-gray-50 dark:bg-gray-700 p-3 rounded-md">
                    {report.moderatorNote}
                  </p>
                </div>
              )}
            </div>
          )}
          
          {report.status === 'pending' && (
            <div className="mt-4 flex justify-end space-x-3">
              <button
                onClick={() => setResolveConfirmation({ ...report, action: 'reject' })}
                className="inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 shadow-sm text-sm font-medium rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                <XCircle className="mr-2 -ml-1 h-5 w-5 text-red-500" />
                Reject
              </button>
              <button
                onClick={() => setResolveConfirmation({ ...report, action: 'approve' })}
                className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                <CheckCircle className="mr-2 -ml-1 h-5 w-5" />
                Approve
              </button>
            </div>
          )}
        </div>
      </div>
    );
  };
  
  return (
    <div className="container mx-auto px-4 py-8">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
          Content Reports
        </h1>
        <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
          Review and manage reported content for your school
        </p>
      </div>

      {/* Report Type Tabs */}
      <div className="mb-6">
        <div className="flex flex-wrap gap-2">
          <TabButton
            type="all"
            label="All Reports"
            icon={Flag}
            count={reportCounts.all}
            active={reportTypeFilter === 'all'}
            onClick={() => setReportTypeFilter('all')}
          />
          <TabButton
            type="material"
            label="Materials"
            icon={FileText}
            count={reportCounts.material}
            active={reportTypeFilter === 'material'}
            onClick={() => setReportTypeFilter('material')}
          />
          <TabButton
            type="comment"
            label="Comments"
            icon={MessageSquare}
            count={reportCounts.comment}
            active={reportTypeFilter === 'comment'}
            onClick={() => setReportTypeFilter('comment')}
          />
          <TabButton
            type="user"
            label="Users"
            icon={UserX}
            count={reportCounts.user}
            active={reportTypeFilter === 'user'}
            onClick={() => setReportTypeFilter('user')}
          />
          <TabButton
            type="professor_rating"
            label="Professor Ratings"
            icon={Star}
            count={reportCounts.professor_rating}
            active={reportTypeFilter === 'professor_rating'}
            onClick={() => setReportTypeFilter('professor_rating')}
          />
          {/* NEW: Flagged Materials Tab */}
          <TabButton
            type="flagged_materials"
            label="Flagged Materials"
            icon={AlertCircle}
            count={reportCounts.flagged_materials}
            active={reportTypeFilter === 'flagged_materials'}
            onClick={() => setReportTypeFilter('flagged_materials')}
            variant="flagged"
          />
        </div>
      </div>
      
      {/* Search and Filters */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4 mb-6">
        <div className="relative mb-4">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <Search className="h-5 w-5 text-gray-400" />
          </div>
          <input
            type="text"
            placeholder="Search reports..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10 pr-4 py-2 w-full border border-gray-300 dark:border-gray-700 rounded-lg focus:ring-blue-500 focus:border-blue-500"
          />
        </div>
        
        <div className="flex justify-between items-center">
          {/* Status filter - Hide when flagged materials filter is active */}
          {reportTypeFilter !== 'flagged_materials' && (
            <div className="flex space-x-2">
              <button
                onClick={() => setStatusFilter('pending')}
                className={`px-3 py-1 rounded-md text-sm font-medium ${
                  statusFilter === 'pending' 
                    ? 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200' 
                    : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200'
                }`}
              >
                Pending
              </button>
              <button
                onClick={() => setStatusFilter('approved')}
                className={`px-3 py-1 rounded-md text-sm font-medium ${
                  statusFilter === 'approved' 
                    ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' 
                    : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200'
                }`}
              >
                Approved
              </button>
              <button
                onClick={() => setStatusFilter('rejected')}
                className={`px-3 py-1 rounded-md text-sm font-medium ${
                  statusFilter === 'rejected' 
                    ? 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200' 
                    : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200'
                }`}
              >
                Rejected
              </button>
              <button
                onClick={() => setStatusFilter('all')}
                className={`px-3 py-1 rounded-md text-sm font-medium ${
                  statusFilter === 'all' 
                    ? 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200' 
                    : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200'
                }`}
              >
                All
              </button>
            </div>
          )}
          
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="flex items-center text-gray-600 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
          >
            <Filter className="h-4 w-4 mr-1" />
            <span className="text-sm">{showFilters ? 'Hide filters' : 'Show filters'}</span>
          </button>
        </div>
        
        {/* NEW: Show flagged materials filter info */}
        {reportTypeFilter === 'flagged_materials' && (
          <div className="mt-4">
            <div className="bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800 rounded-md p-3">
              <div className="flex items-center">
                <AlertCircle className="h-5 w-5 text-orange-500 mr-2" />
                <span className="text-sm font-medium text-orange-800 dark:text-orange-200">
                  Showing materials automatically flagged for academic integrity violations
                </span>
              </div>
              <p className="text-xs text-orange-600 dark:text-orange-300 mt-1">
                These materials were flagged by AI analysis for potential policy violations and require review.
              </p>
            </div>
          </div>
        )}
        
        {showFilters && (
          <div className="mt-4 p-4 bg-gray-50 dark:bg-gray-700 rounded-lg border border-gray-200 dark:border-gray-600">
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Additional filters will be added in a future update.
            </p>
          </div>
        )}
      </div>
      
      {/* Reports Content */}
      {loading ? (
        <div className="flex justify-center items-center py-12">
          <Loader className="h-8 w-8 text-blue-500 animate-spin mr-2" />
          <span className="text-gray-600 dark:text-gray-400">Loading reports...</span>
        </div>
      ) : error ? (
        <div className="bg-red-100 dark:bg-red-900 border border-red-400 dark:border-red-700 text-red-700 dark:text-red-300 px-4 py-3 rounded relative mb-6" role="alert">
          <strong className="font-bold">Error! </strong>
          <span className="block sm:inline">{error}</span>
        </div>
      ) : filteredReports.length === 0 ? (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-8 text-center">
          <Flag className="mx-auto h-12 w-12 text-gray-400 dark:text-gray-600" />
          <h3 className="mt-4 text-lg font-medium text-gray-900 dark:text-white">
            No reports found
          </h3>
          <p className="mt-1 text-gray-500 dark:text-gray-400">
            {searchQuery ? 
              'No reports match your search criteria.' : 
              reportTypeFilter === 'flagged_materials' 
                ? 'No flagged materials found. All materials are compliant! 🎉'
                : `No ${statusFilter !== 'all' ? statusFilter : ''} ${reportTypeFilter !== 'all' ? reportTypeFilter : ''} reports available.`}
          </p>
          {searchQuery && (
            <button
              onClick={() => setSearchQuery('')}
              className="mt-4 text-blue-600 dark:text-blue-400 hover:underline"
            >
              Clear search
            </button>
          )}
        </div>
      ) : (
        <div className="space-y-6">
          {filteredReports.map((report) => (
            <div key={report.id}>
              {renderReportContent(report)}
            </div>
          ))}
        </div>
      )}
      
      {/* Resolve Confirmation Modal */}
      {resolveConfirmation && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full">
            <div className="flex items-center mb-4">
              <div className={`p-2 rounded-full ${
                resolveConfirmation.action === 'approve' 
                  ? 'bg-green-100 text-green-600 dark:bg-green-900 dark:text-green-400' 
                  : 'bg-red-100 text-red-600 dark:bg-red-900 dark:text-red-400'
              } mr-3`}>
                {resolveConfirmation.action === 'approve' 
                  ? <CheckCircle className="h-6 w-6" /> 
                  : <XCircle className="h-6 w-6" />
                }
              </div>
              <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                {resolveConfirmation.action === 'approve' ? 'Approve Report' : 'Reject Report'}
              </h3>
            </div>
            
            <p className="text-gray-600 dark:text-gray-400 mb-4">
              {resolveConfirmation.action === 'approve' ? (
                resolveConfirmation.reportType === 'user' ?
                  'Approving this report will allow you to take action on the user account.' :
                  resolveConfirmation.reportType === 'professor_rating' ?
                    'Approving this report will remove the professor rating from the platform.' :
                    `Approving this report will remove the ${resolveConfirmation.reportType} from the platform.`
              ) : (
                `Rejecting this report will keep the ${resolveConfirmation.reportType} on the platform.`
              )}
            </p>
            
            {/* User action section (only for user reports being approved) */}
            {resolveConfirmation.reportType === 'user' && resolveConfirmation.action === 'approve' && (
              <div className="mb-4 p-4 bg-gray-50 dark:bg-gray-700 rounded-md">
                <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  User Action:
                </h4>
                <div className="space-y-3">
                  <div className="flex items-center">
                    <input
                      id="action-none"
                      type="radio"
                      name="user-action"
                      value="none"
                      checked={userAction === 'none'}
                      onChange={() => setUserAction('none')}
                      className="h-4 w-4 text-red-600 focus:ring-red-500 border-gray-300 dark:border-gray-600"
                    />
                    <label htmlFor="action-none" className="ml-2 block text-sm text-gray-700 dark:text-gray-300">
                      No action (just flag the user)
                    </label>
                  </div>
                  
                  <div className="flex items-center">
                    <input
                      id="action-suspend"
                      type="radio"
                      name="user-action"
                      value="suspend"
                      checked={userAction === 'suspend'}
                      onChange={() => setUserAction('suspend')}
                      className="h-4 w-4 text-red-600 focus:ring-red-500 border-gray-300 dark:border-gray-600"
                    />
                    <label htmlFor="action-suspend" className="ml-2 block text-sm text-gray-700 dark:text-gray-300">
                      Suspend user temporarily
                    </label>
                  </div>
                  
                  <div className="flex items-center">
                    <input
                      id="action-ban"
                      type="radio"
                      name="user-action"
                      value="ban"
                      checked={userAction === 'ban'}
                      onChange={() => setUserAction('ban')}
                      className="h-4 w-4 text-red-600 focus:ring-red-500 border-gray-300 dark:border-gray-600"
                    />
                    <label htmlFor="action-ban" className="ml-2 block text-sm text-gray-700 dark:text-gray-300">
                      Ban user permanently
                    </label>
                  </div>
                  
                  {/* Suspension duration input (only shown when suspend is selected) */}
                  {userAction === 'suspend' && (
                    <div className="mt-3 pl-6">
                      <label htmlFor="suspension-days" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Suspension Duration (days):
                      </label>
                      <input
                        id="suspension-days"
                        type="number"
                        min="1"
                        max="365"
                        value={suspensionDays}
                        onChange={(e) => setSuspensionDays(Math.max(1, parseInt(e.target.value) || 1))}
                        className="mt-1 block w-24 rounded-md border-gray-300 dark:border-gray-700 shadow-sm focus:border-red-500 focus:ring-red-500 sm:text-sm dark:bg-gray-800 dark:text-white"
                      />
                    </div>
                  )}
                </div>
              </div>
            )}
            
            <div className="mb-4">
              <label htmlFor="resolution-note" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                {resolveConfirmation.reportType === 'user' && (userAction === 'suspend' || userAction === 'ban') 
                  ? 'Reason for action (will be sent to user):' 
                  : 'Add a note (optional):'}
              </label>
              <textarea
                id="resolution-note"
                rows="3"
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                placeholder={resolveConfirmation.reportType === 'user' && (userAction === 'suspend' || userAction === 'ban')
                  ? "Explain why this action is being taken..."
                  : "Add a note about your decision..."}
                value={resolveConfirmation.note || ''}
                onChange={(e) => setResolveConfirmation({ ...resolveConfirmation, note: e.target.value })}
                required={resolveConfirmation.reportType === 'user' && (userAction === 'suspend' || userAction === 'ban')}
              ></textarea>
              {resolveConfirmation.reportType === 'user' && (userAction === 'suspend' || userAction === 'ban') && (
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  This reason will be included in the notification email sent to the user.
                </p>
              )}
            </div>
            
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setResolveConfirmation(null)}
                className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  // Check if a reason is required but not provided
                  if (resolveConfirmation.reportType === 'user' && 
                      (userAction === 'suspend' || userAction === 'ban') && 
                      !resolveConfirmation.note) {
                    toast.error("Please provide a reason for this action");
                    return;
                  }
                  
                  handleResolveReport(
                    resolveConfirmation.id, 
                    resolveConfirmation.action === 'approve' ? 'approve' : 'reject'
                  );
                }}
                className={`px-4 py-2 text-white rounded-md ${
                  resolveConfirmation.action === 'approve' 
                    ? 'bg-green-600 hover:bg-green-700' 
                    : 'bg-red-600 hover:bg-red-700'
                }`}
              >
                {resolveConfirmation.action === 'approve' ? 'Approve' : 'Reject'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ModeratorReportsPage;